import {
  BulkDeleteContractInput,
  ContractStatusBulkUpdateInput,
  CreateNewContractInput,
  GetContractInput,
  GetServiceEntriesInput,
  ReassignContractInput,
  UpdateContractInput,
  UpdateServiceBillingSettingsInput,
} from "shared";
import { axiosClient } from "utilz";
import {
  apiErrorHandler,
  generateUrlString,
  getQueryDataObject,
} from "utilz/helpers";

export const getAllCurrency = async () => {
  try {
    const url = "/v1/currency";
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getAllContracts = async ({
  perPageCount,
  pageNumber,
  sortColumn = "created_at",
  sortDirection = "desc",
  searchQuery,
  customers,
  contractEndDate,
  contractEndDateRange,
  contractStartDate,
  contractStartDateRange,
  contractStatus,
  contractType,
  includeItems,
}: GetContractInput) => {
  try {
    const url = "/v2/contracts";
    const params = {
      searchQuery,
      sortDirection,
      sortColumn,
      page: pageNumber,
      per_page: perPageCount,
      customers: JSON.stringify(customers ? customers : []),
      ...(contractType && {
        contractType: JSON.stringify(contractType),
      }),
      ...(contractEndDate && { contractEndDate: contractEndDate }),
      ...(contractEndDateRange && {
        contractEndDateRange: contractEndDateRange,
      }),
      ...(contractStartDate && { contractStartDate: contractStartDate }),
      ...(contractStartDateRange && {
        contractStartDateRange: contractStartDateRange,
      }),
      ...(contractStatus && { contractStatus: contractStatus }),
      ...(includeItems && { include_items: true }),
    };

    const response = await axiosClient.get(`${url}`, {
      params,
    });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getContractById = async (id: string) => {
  try {
    const url = `/v2/contracts/${id}`;
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const createContract = async (data: CreateNewContractInput) => {
  try {
    const url = "/v1/contracts";
    const response = await axiosClient.post(`${url}`, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData;
    } else {
      // throw resData?.message;
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateContract = async ({
  contractId,
  data,
}: UpdateContractInput) => {
  try {
    const url = `/v1/contracts/${contractId}`;
    const response = await axiosClient.patch(`${url}`, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData;
    } else {
      // Throw the response, not the message string — apiErrorHandler reads
      // `.data.message`, so a bare string lost the backend's error text.
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const contractStatusBulkUpdate = async (
  data: ContractStatusBulkUpdateInput,
) => {
  try {
    const url = "/v1/contracts/status";
    const response = await axiosClient.patch(`${url}`, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getBillingContractDropDownData = async (
  customerId: string,
  serviceType: string,
) => {
  try {
    const url = await generateUrlString({
      baseUrl: "/v1/contract/dropdown",
      data: {
        customer_id: customerId,
        service_type: serviceType,
      },
    });
    // const url = "/v1/contract/dropdown";
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getBilligDefaultContract = async (
  customerId: string,
  serviceType: string,
) => {
  try {
    const url = await generateUrlString({
      baseUrl: "/v1/default/contracts",
      data: {
        customer_id: customerId,
        service_type: serviceType,
      },
    });
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw resData?.message;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateServiceContractById = async (
  serviceId: string,
  serviceType: string,
  contractId: string,
) => {
  try {
    const data = {
      contract_id: contractId,
    };
    const url = `/contract/${serviceType}/${serviceId}`;
    const response = await axiosClient.patch(url, data);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getServicesByContractId = async (contractId: string) => {
  try {
    const url = `/v1/billing/services?contract_id=${contractId}`;
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getServiceEntries = async ({
  projectIds,
  ticketIds,
}: GetServiceEntriesInput) => {
  try {
    const data = {
      project_ids: projectIds,
      ticket_ids: ticketIds,
    };
    const url = "/v1/billing/services/entries";
    const response = await axiosClient.post(`${url}`, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const reassignContract = async ({
  contractId,
  reassignToId,
}: ReassignContractInput) => {
  try {
    const data = {
      contract_id: reassignToId,
    };
    const url = `/contract/inactive/re-assign/${contractId}`;
    const response = await axiosClient.patch(`${url}`, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const deleteContract = async (contractId: string) => {
  try {
    const url = `/v1/contracts/${contractId}`;
    const response = await axiosClient.delete(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const bulkDeleteContract = async (data: BulkDeleteContractInput) => {
  try {
    const url = "/v1/contracts/bulk-delete";
    const response = await axiosClient.post(`${url}`, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getContractsByContractId = async (contractId: string) => {
  try {
    const url = `/v1/billing/services?contract_id=${contractId}`;
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateServiceBillingSettings = async ({
  service_type,
  service_id,
  ...data
}: UpdateServiceBillingSettingsInput) => {
  try {
    const url = `/billing-details/${service_type}/${service_id}`;
    const response = await axiosClient.patch(`${url}`, data);
    const resData = response?.data;
    if (resData?.result?.success && resData?.result?.statusCode === 200) {
      return resData;
    } else {
      throw new Error(resData?.result?.message);
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};
